import { Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CsrService } from "../services/csr.service";
import { CommonService } from "../common/common.service";
import { Router } from "@angular/router";
import { GoogleAnalyticsService } from "../services/google-analytics.service";
import { fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CmnIconName } from "../shared/components/cmn-icon/cmn-icon.component";


interface HeaderMenuItem {
  title: string;
  icon: CmnIconName;
  action: () => void;
  hover: string;
  route: string;
}

@Component({
  selector: "app-header-bar",
  templateUrl: "./header-bar.component.html",
  styleUrls: ["./header-bar.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class HeaderBarComponent implements OnInit, OnDestroy {
  readonly CmnIconName = CmnIconName;

  @Input() isHidden = false;
  @Input() hideCMNBranding = false;
  @Input() hideAbout = false;
  lastScrollPosition = 0;
  isMobile: boolean = false;
  menuOpen = false;
  private scrollSubscription: Subscription | null = null;

  MENU = {
    HOME: 'HOME',
    ABOUT: 'ABOUT',
    CONTACT: 'CONTACT US',
    NON_CMN_CONTACT: 'CONTACT DONOR SERVICES'
  };

  private allMenuItems: HeaderMenuItem[] =  [
    { title: this.MENU.HOME, icon: CmnIconName.Home, action: () => this.homeClicked(), hover: 'Back to home', route: '/splash' },
    { title: this.MENU.ABOUT, icon: CmnIconName.Bookmark, action: () => this.aboutClicked(), hover: '', route: '/about' },
    { title: this.MENU.CONTACT, icon: CmnIconName.Phone, action: () => this.contactClicked(), hover: '', route: '/contact' },
  ];

  get menuItems() {
    if (!this.hideCMNBranding && !this.hideAbout) {
      return this.allMenuItems;
    }

    return this.allMenuItems
      .filter(((item) => !this.hideAbout || item.title !== this.MENU.ABOUT))
      .map(item => {
        if (this.hideCMNBranding && item.title === this.MENU.CONTACT) {
          return { ...item, title: this.MENU.NON_CMN_CONTACT };
        }

        return item;
      });
  }

  constructor(
    private csrService: CsrService,
    private _commonService: CommonService,
    private router: Router,
    private _googleAnalytics: GoogleAnalyticsService
  ) { }

  ngOnInit() {
    this.checkIfMobile();
    this.listenToScroll();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkIfMobile();
    if (!this.isMobile) {
      this.isHidden = false;
    }
    this.listenToScroll();
  }

  checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;

    if (this.isMobile) {
      this.menuOpen = false;
    }
  }

  listenToScroll() {
    this.unsubscribeScroll();
    if (this.isMobile) {
      this.scrollSubscription = fromEvent(window, 'scroll')
        .pipe(throttleTime(100))
        .subscribe(() => {
          const scrollPosition = window.pageYOffset;
          const threshold = 100;
          if (Math.abs(scrollPosition - this.lastScrollPosition) > threshold) {
            this.isHidden = scrollPosition > this.lastScrollPosition;
            this.lastScrollPosition = scrollPosition;
          }
        });
    }
  }

  private unsubscribeScroll() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
    }
  }

  getCampaignId(): string | null {
    return this.csrService.campaignId();
  }

  aboutClicked = (): void => {
    this.navigateWithTracking(['/about'], () => this._commonService.aboutShuffel(), "About");
  }

  contactClicked = (): void => {
    this.navigateWithTracking(['/contact'], () => this._commonService.contactShuffel(), "Contact");
  }

  homeClicked = (): void => {
    const campaignId = this.getCampaignId();
    const hospitalId = this.csrService.hospitalId();
    const radioStationId = this.csrService.radioStationId();
    const route = ['/splash', campaignId !== null ? campaignId.toString() : ''];
    const hasHospitalId = hospitalId && hospitalId !== '0';
    const hasRadioStationId = radioStationId && radioStationId !== '0';

    if (hasRadioStationId) {
      route.push(hospitalId || '0');
      route.push(radioStationId);
    } else if (hasHospitalId) {
      route.push(hospitalId);
    }

    this.navigateWithTracking(route, () => this._commonService.homeShuffel(), "Splash");
  }

  isMenuItemActive(route: string): boolean {
    return this.router.url === route || this.router.url.startsWith(`${route}/`);
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  private navigateWithTracking(route: string[], action: () => void, event: string): void {
    this.closeMenu();
    action();
    this.trackItemSelected(event);
    setTimeout(() => {
      this.router.navigate(route);
    }, 1000);
  }

  ngOnDestroy() {
    this.unsubscribeScroll();
  }

  private trackItemSelected(event: string): void {
    this._googleAnalytics.customEventEmitter({
      eventName: event,
      eventCategory: 'Splash - Menu',
      eventAction: 'Click',
      eventLabel: 'User enter in Menu'
    });
  }
}
